// AUTO-GENERATED from servers/common-servers-v2/helpers/FormHelper/resolve_form_state.js by servers/common-servers-v2/scripts/sync-form-helper.js.
// Do not edit: change the source and run `node scripts/sync-form-helper.js`.
/* eslint-disable */
import { LIMITS } from "./constants.js";
import { evaluate_conditions } from "./conditions.js";

/*
 * resolve_form_state(version, values) -> what a renderer / the server must treat
 * as visible, disabled and required right now (spec §4, §6).
 *
 *   {
 *     groups:    { [group_key]: { visible, disabled, required } },
 *     fields:    { [field_key]: { visible, disabled, required } },   // fields of non-repeatable groups
 *     instances: { [group_key]: [ { [field_key]: { visible, ... } }, ... ] },  // repeatable groups, one per instance
 *     cyclic:    [ key, ... ],   // nodes whose conditions depend on themselves (also rejected at save time)
 *   }
 *
 * Values shape (spec §10): fields of non-repeatable groups sit flat under their
 * field key; a repeatable group sits under its group key as an array of
 * { [field_key]: value } objects.
 *
 * A field is visible only when its group is; disabled / required when the group
 * or the field says so. A hidden field counts as empty in other fields'
 * conditions. Inside a repeatable group a condition reads the same instance
 * first; fields of other repeatable groups are never readable.
 */

const is_plain_object = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

const has_own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const as_list = (value) => (Array.isArray(value) ? value : []);

// show / disabled / required of one group or field from its own constants and conditions.
const node_flags = (node, get) => {
    const disabled_list = as_list(node?.disabled_conditions);
    const required_list = as_list(node?.required_conditions);
    return {
        visible: node?.is_show !== false && evaluate_conditions(node?.show_conditions, get),
        disabled: node?.is_disabled === true || (disabled_list.length > 0 && evaluate_conditions(disabled_list, get)),
        required: node?.is_required === true || (required_list.length > 0 && evaluate_conditions(required_list, get)),
    };
};

const combine = (group_flags, field_flags) => ({
    visible: group_flags.visible && field_flags.visible,
    disabled: group_flags.disabled || field_flags.disabled,
    required: group_flags.required || field_flags.required,
});

const HIDDEN = Object.freeze({ visible: false, disabled: false, required: false });

const instance_count = (group, values) => {
    const list = values[group.key];
    if (!Array.isArray(list) || list.length === 0) {
        return 1;
    }
    return Math.min(list.length, LIMITS.REPEAT_MAX);
};

const resolve_form_state = (version, values) => {
    const group_list = as_list(version?.groups);
    const data = is_plain_object(values) ? values : {};

    const field_info = {};
    group_list.forEach((group) => {
        as_list(group?.fields).forEach((field) => {
            if (field && typeof field.key === "string" && !has_own(field_info, field.key)) {
                field_info[field.key] = { field, group, repeatable: group.is_repeatable === true };
            }
        });
    });

    const cyclic = [];
    const mark_cyclic = (key) => {
        if (!cyclic.includes(key)) {
            cyclic.push(key);
        }
    };

    // Group flags are always read from the global (non-repeatable) scope.
    const group_cache = {};
    const group_busy = {};
    const resolve_group = (group) => {
        if (has_own(group_cache, group.key)) {
            return group_cache[group.key];
        }
        if (group_busy[group.key]) {
            mark_cyclic(group.key);
            return HIDDEN;
        }
        group_busy[group.key] = true;
        // eslint-disable-next-line no-use-before-define
        const flags = node_flags(group, global_get);
        group_busy[group.key] = false;
        group_cache[group.key] = flags;
        return flags;
    };

    const global_cache = {};
    const global_busy = {};
    const resolve_global_field = (key) => {
        if (has_own(global_cache, key)) {
            return global_cache[key];
        }
        if (global_busy[key]) {
            mark_cyclic(key);
            return HIDDEN;
        }
        const { field, group } = field_info[key];
        global_busy[key] = true;
        const state = combine(resolve_group(group), node_flags(field, global_get)); // eslint-disable-line no-use-before-define
        global_busy[key] = false;
        global_cache[key] = state;
        return state;
    };

    // Value of a field of a non-repeatable group, seen by everyone: hidden -> undefined.
    function global_get(path) {
        if (typeof path !== "string" || !has_own(field_info, path) || field_info[path].repeatable) {
            return undefined;
        }
        return resolve_global_field(path).visible ? data[path] : undefined;
    }

    const state = { groups: {}, fields: {}, instances: {}, cyclic };

    group_list.forEach((group) => {
        if (!group || typeof group.key !== "string") {
            return;
        }
        const group_state = resolve_group(group);
        state.groups[group.key] = group_state;

        if (group.is_repeatable !== true) {
            as_list(group.fields).forEach((field) => {
                if (field && typeof field.key === "string" && field_info[field.key]?.field === field) {
                    state.fields[field.key] = resolve_global_field(field.key);
                }
            });
            return;
        }

        const count = instance_count(group, data);
        const raw_list = Array.isArray(data[group.key]) ? data[group.key] : [];
        state.instances[group.key] = [];
        for (let index = 0; index < count; index += 1) {
            const instance_values = is_plain_object(raw_list[index]) ? raw_list[index] : {};
            const cache = {};
            const busy = {};
            const resolve_instance_field = (key) => {
                if (has_own(cache, key)) {
                    return cache[key];
                }
                if (busy[key]) {
                    mark_cyclic(key);
                    return HIDDEN;
                }
                busy[key] = true;
                // eslint-disable-next-line no-use-before-define
                const field_state = combine(group_state, node_flags(field_info[key].field, instance_get));
                busy[key] = false;
                cache[key] = field_state;
                return field_state;
            };
            // Same-instance fields first, then the global scope; other repeatable groups are invisible.
            function instance_get(path) {
                if (typeof path === "string" && has_own(field_info, path) && field_info[path].group === group) {
                    return resolve_instance_field(path).visible ? instance_values[path] : undefined;
                }
                return global_get(path);
            }
            const instance_state = {};
            as_list(group.fields).forEach((field) => {
                if (field && typeof field.key === "string" && field_info[field.key]?.field === field) {
                    instance_state[field.key] = resolve_instance_field(field.key);
                }
            });
            state.instances[group.key].push(instance_state);
        }
    });

    return state;
};

// State of one field: pass the repeat index for a field inside a repeatable group.
const get_field_state = (state, field_key, index = 0) => {
    if (state?.fields && has_own(state.fields, field_key)) {
        return state.fields[field_key];
    }
    const groups = Object.keys(state?.instances || {});
    for (let i = 0; i < groups.length; i += 1) {
        const instance = state.instances[groups[i]][index];
        if (instance && has_own(instance, field_key)) {
            return instance[field_key];
        }
    }
    return HIDDEN;
};

export {
    resolve_form_state,
    get_field_state,
};
